import { useState } from "react";
import { ListItem, ListItemButton, ListItemText, List, ListItemIcon, Checkbox } from "@mui/material";
import { styled } from "@mui/styles";
import { useTranslation } from "react-i18next";
import { SelectList } from "components";
import { LangConstant, SystemConstant } from "const";
import { getDayStartAndEnd } from "utils/date.utils";
import theme from "theme/material";
import { getNSLang } from "utils/lang.utils";

export const FILTER_BY_TIME = {
  allTime: "allTime",
  today: "today",
  yesterday: "yesterday",
  last7Day: "last7Day",
  last30Day: "last30Day",
};

export const FILTER_FILE_TYPE = {
  pdf: SystemConstant.FILE_TYPE.PDF,
  image: SystemConstant.FILE_TYPE.IMAGE,
  video: SystemConstant.FILE_TYPE.VIDEO,
  audio: SystemConstant.FILE_TYPE.AUDIO,
  otherFile: SystemConstant.FILE_TYPE.OTHER,
};

export const PAGING_SIZE = 15;

export const DEFAULT_FILTER = {
  isAll: true,
  createdByMe: false,
  createdByAccountIds: null,
  fileType: null,
  startDate: null,
  endDate: null,
  offset: 0,
  limit: PAGING_SIZE,
};

export const OTHER_FILE_TYPES = [
  SystemConstant.FILE_TYPE.WORD,
  SystemConstant.FILE_TYPE.EXCEL,
  SystemConstant.FILE_TYPE.PPT,
];

const FILE_FILTER_OPTIONS = [
  {
    label: getNSLang(LangConstant.NS_MANAGE_FILE, "TXT_PDF"),
    value: FILTER_FILE_TYPE.pdf,
  },
  {
    label: getNSLang(LangConstant.NS_MANAGE_FILE, "TXT_IMAGE"),
    value: FILTER_FILE_TYPE.image,
  },
  {
    label: getNSLang(LangConstant.NS_MANAGE_FILE, "TXT_VIDEO"),
    value: FILTER_FILE_TYPE.video,
  },
  {
    label: getNSLang(LangConstant.NS_MANAGE_FILE, "TXT_AUDIO"),
    value: FILTER_FILE_TYPE.audio,
  },
  {
    label: getNSLang(LangConstant.NS_MANAGE_FILE, "TXT_OTHER_FILE"),
    value: FILTER_FILE_TYPE.otherFile,
  },
];

export const TimeFilter = ({ onChangeTime }) => {
  const { t: getLabel } = useTranslation(LangConstant.NS_MANAGE_FILE);
  const defaultText = getLabel("TXT_TIME");

  const [selectedText, setSelectedText] = useState(defaultText);

  const handleClickItemFunc = item => () => {
    setSelectedText(item.value === FILTER_BY_TIME.allTime ? defaultText : item.label);

    const selectDate = new Date();
    const todayDate = getDayStartAndEnd(new Date());
    if (onChangeTime) {
      switch (item.value) {
        case FILTER_BY_TIME.allTime:
          onChangeTime(); // All tim
          break;

        case FILTER_BY_TIME.today:
          onChangeTime(todayDate.start.getTime(), todayDate.end.getTime());
          break;

        case FILTER_BY_TIME.yesterday:
          selectDate.setDate(selectDate.getDate() - 1);
          const yesterdayDate = getDayStartAndEnd(selectDate);
          onChangeTime(yesterdayDate.start.getTime(), yesterdayDate.end.getTime());
          break;

        case FILTER_BY_TIME.last7Day:
          selectDate.setDate(selectDate.getDate() - 7);
          const last7Date = getDayStartAndEnd(selectDate);
          onChangeTime(last7Date.start.getTime(), todayDate.end.getTime());
          break;

        case FILTER_BY_TIME.last30Day:
          selectDate.setDate(selectDate.getDate() - 30);
          const last30Date = getDayStartAndEnd(selectDate);
          onChangeTime(last30Date.start.getTime(), todayDate.end.getTime());
          break;

        default:
          break;
      }
    }
  };

  const arrayLabel = [
    {
      label: getLabel("TXT_ALL_TIME"),
      value: FILTER_BY_TIME.allTime,
    },
    {
      label: getLabel("TXT_TODAY"),
      value: FILTER_BY_TIME.today,
    },
    {
      label: getLabel("TXT_YESTERDAY"),
      value: FILTER_BY_TIME.yesterday,
    },
    {
      label: getLabel("TXT_LAST_7DAY"),
      value: FILTER_BY_TIME.last7Day,
    },
    {
      label: getLabel("TXT_LAST_30DAY"),
      value: FILTER_BY_TIME.last30Day,
    },
  ];

  return (
    <SelectList selectedText={selectedText} PopperProps={{ sx: { zIndex: theme.zIndex.drawer + 1 } }}>
      <List>
        {arrayLabel.map(item => (
          <ListItem disablePadding key={item.value} onClick={handleClickItemFunc(item)}>
            <ItemButton>
              <ListItemText primary={item.label} />
            </ItemButton>
          </ListItem>
        ))}
      </List>
    </SelectList>
  );
};

export const FileFilter = ({ onChangeFileType, arrayLabel = FILE_FILTER_OPTIONS }) => {
  const { t: getLabel } = useTranslation(LangConstant.NS_MANAGE_FILE);
  const defaultText = getLabel("TXT_FILE_TYPE");

  const [selectedText, setSelectedText] = useState(defaultText);
  const [fileTypeList, setFileTypeList] = useState([]);

  const handleClickItemFunc = item => () => {
    const currentIndex = fileTypeList.indexOf(item.value);
    const newTypeList = [...fileTypeList];

    if (currentIndex === -1) {
      newTypeList.push(item.value);
    } else {
      newTypeList.splice(currentIndex, 1);
    }

    switch (newTypeList.length) {
      case 0:
        setSelectedText(defaultText);
        break;

      case 1:
        const selectedItem = arrayLabel.find(typeItem => typeItem.value === newTypeList[0]);
        setSelectedText(selectedItem.label);
        break;

      default:
        setSelectedText(getLabel("FM_MORE_FILE_TYPE", { quantity: newTypeList.length }));
        break;
    }

    setFileTypeList(newTypeList);
    if (onChangeFileType) onChangeFileType(newTypeList);
  };

  return (
    <SelectList
      selectedText={selectedText}
      disableClickItemEvent
      PopperProps={{ sx: { zIndex: theme.zIndex.drawer + 1 } }}
    >
      <List>
        {arrayLabel.map(item => (
          <ListItem disablePadding key={item.value} onClick={handleClickItemFunc(item)}>
            <ItemButton>
              <ItemIcon>
                <CheckboxIcon
                  edge="start"
                  checked={fileTypeList.indexOf(item.value) !== -1}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ "aria-labelledby": item.value }}
                />
              </ItemIcon>
              <ListItemText primary={item.label} />
            </ItemButton>
          </ListItem>
        ))}
      </List>
    </SelectList>
  );
};

const ItemButton = styled(ListItemButton)(props => ({
  minWidth: 156,

  "&:hover": {
    background: props.theme.palette.primary.main,
    "&, & *": { color: "white" },
  },
}));

const ItemIcon = styled(ListItemIcon)({
  minWidth: 36,
  paddingLeft: 6,
});

const CheckboxIcon = styled(Checkbox)({
  padding: 0,
});
